/* global module window setTimeout document */

import React from 'react';
import numeral from 'numeral';
import StackedBar from '../../../d3/Stacked';
import get from 'lodash/get';
import { connect } from 'react-redux';
import { Label } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { withT, wrap, withRouterAndHelpers } from '../../../lib/wrappers';

import s from '../../../redux/selectors';
import { userApiValue, defaultApiValue } from '../../../lib/legacyHelpers';
import { CATEGORY_COLORS } from '../../../lib/colors';
import { routes } from '../../../lib/routerHelper';
import ResizableComponent from '../../../lib/base_classes/ResizableComponent';

const lock = '/assets/img/lock.svg';
const lockOpen = '/assets/img/lock_open.svg';

const CATEGORIES = ['result_transport_total', 'result_housing_total',
  'result_food_total', 'result_goods_total', 'result_services_total'];
const categoryTitles = {
  result_transport_total: 'Travel',
  result_housing_total: 'Home',
  result_food_total: 'Food',
  result_goods_total: 'Goods',
  result_services_total: 'Services',
  all_services: 'All',
  reduction: 'Reduction',
};
const MIN_GRAPH_WIDTH = 250;
const MAX_GRAPH_WIDTH = 800;

const graphMarginLeft = 40;

const calculateCategoryPercent = (apiKey, userFootprint, averageFootprint) => {
  const userCategoryTotal = parseFloat(userApiValue(apiKey, userFootprint, averageFootprint));
  const defaultCategoryTotal = defaultApiValue(apiKey, averageFootprint);
  return Math.abs((100 * (userCategoryTotal / defaultCategoryTotal)) - 100);
};

const isBetterThanAverage = (apiKey, userFootprint, averageFootprint) => {
  const userCategoryTotal = parseFloat(userApiValue(apiKey, userFootprint, averageFootprint));
  const defaultCategoryTotal = defaultApiValue(apiKey, averageFootprint);
  return Math.round((100 * (userCategoryTotal / defaultCategoryTotal)) - 100) <= 0;
};
const isBetterThanAverageText = (apiKey, userFootprint, averageFootprint, t) => {
  if (Math.round(calculateCategoryPercent(apiKey, userFootprint, averageFootprint)) === 0) {
    return t('same');
  }
  if (isBetterThanAverage(apiKey, userFootprint, averageFootprint)){
    return t('better');
  }
  return t('worse');
};

const formatString = (str, ...args) => {
  let i = 0;
  return str.replace(/{}/g, () => {
    if (typeof args[i] !== 'undefined') {
      i += 1;
      return args[i - 1];
    }
    return '';
  });
};

class GraphsComponent extends ResizableComponent {

  constructor(props, context) {
    super(props, context);
    const graphs = this;
    graphs.initResizeListener();
    this.state = {
      lockedY: false
    }
  }

  componentDidMount() {
    const graphs = this;
    graphs.initializeOverallBarChart();
  }

  shouldComponentUpdate(nextProps) {
    return !nextProps.average_footprint.loading && !nextProps.user_footprint.loading;
  }

  componentDidUpdate(lastProps) {
    const { active } = this.props;
    if (active) {
      if (!lastProps.active) {
        setTimeout(() => {
          this.initializeOverallBarChart();
        }, 1200);
      } else {
        this.initializeOverallBarChart();
      }
    }
    this.hidePopovers();
    if (this.userGrandTotal !== this.lastUserGrandTotal && window.parentIFrame) {
      this.lastUserGrandTotal = this.userGrandTotal;      
      const message = {
        type: 'onChange',
        data: {
          value: this.lastUserGrandTotal,
        },
      };
      window.parentIFrame.sendMessage(JSON.stringify(message));
    }
  }

  componentWillUnmount() {
    this.hidePopovers();
  }

  get categories() {
    return CATEGORIES.map(category_key => this.props.t(get(categoryTitles, category_key)));
  }

  get category_colors() {
    const color_coding = {};
    return CATEGORIES.reduce((hash, category_key) => {
      const translated = this.props.t(get(categoryTitles, category_key));
      color_coding[translated] = CATEGORY_COLORS[category_key];
      return color_coding;
    }, {});
  }

  get user_profile_footprint() {
    return this.props.user_profile_footprint ? this.props.user_profile_footprint : {};
  }

  get user_footprint() {
    return (Object.keys(this.user_profile_footprint).length !== 0) ?
      this.user_profile_footprint :
      this.props.user_footprint.compute_footprint;
  }

  get average_footprint() {
    return this.props.average_footprint.compute_fooptrint;
  }

  get is_takeaction_page() {
    return this.props.currentRoute === routes.TAKE_ACTION;
  }

  get average_footprint_total() {
    const { average_footprint } = this.props;
    return defaultApiValue('result_grand_total', average_footprint);
  }

  get displayCompactSummary() {
    return this.props.currentRoute === routes.FOOTPRINT;
  }

  hidePopovers() {
    window.jQuery('.popover').remove();
  }

  generateData(footprint) {
    return CATEGORIES.map(category => footprint[category]);
  }

  resize() {
    const graphs = this;
    graphs.bar_chart.redraw(graphs.graph_dimensions);
    if (graphs.pie_chart) {
      graphs.pie_chart.redraw(graphs.graph_dimensions);
    }
  }

  get graph_dimensions() {
    const width = document.getElementById('graphs').offsetWidth;
    const dimensions = {
      outer_width: width,
    };
    dimensions.outer_width = Math.max(
      MIN_GRAPH_WIDTH,
      dimensions.outer_width,
    );
    dimensions.outer_width = Math.min(
      MAX_GRAPH_WIDTH,
      dimensions.outer_width,
    );
    dimensions.outer_height = Math.max(300, dimensions.outer_width / 2);
    return dimensions;
  }

  /*
   * Bar Chart
   */

  initializeOverallBarChart() {
    const graphs = this;
    const dimensions = graphs.graph_dimensions;
    const rect_color = get(this.props.ui, 'widget.options.highlight_color', '#0d793e');
    const graph_color = get(this.props.ui, 'widget.options.graph_colors', {
      default: rect_color,
    });
    document.getElementById('overview_bar_chart').innerHTML = '';
    graphs.bar_chart = new StackedBar({
      outer_height: dimensions.outer_height,
      outer_width: dimensions.outer_width,
      container: '#overview_bar_chart',
      y_ticks: 5,
      locked_y: this.state.lockedY,
      last_ticks: this.last_ticks,
      rect_colors: graph_color,
      margin: { top: 4, bottom: 30, left: graphMarginLeft, right: 0 },
      makeTooltipHtml: (item) => {
        const units = this.props.t('tons CO<sub>2</sub>', { ns: 'units' });
        return `<b>${item.title}</b> <br> ${numeral(item.value).format('0,0.00')} ${units}`;
      },
      onDefineTicks: (ticks) => {
        this.last_ticks = ticks;
        // console.log("Setting last ticks", ticks)
      }
    });
    graphs.drawBarData();
  }

  drawBarData() {
    const { stackedBarChartData } = this.props;
    this.bar_chart.drawData(stackedBarChartData);
  }

  /*
   * Summary
   */

  get category_keys() {
    let keys;
    switch (this.props.currentRoute) {
      case routes.TRAVEL:
        keys = ['result_transport_total'];
        break;
      case routes.HOME:
        keys = ['result_housing_total'];
        break;
      case routes.FOOD:
        keys = ['result_food_total'];
        break;
      case routes.SHOPPING:
        keys = ['result_goods_total', 'result_services_total'];
        break;
      default:
        keys = ['result_grand_total'];
    }
    return keys;
  }

  get user_category_footprint() {
    const graphs = this;
    if (!this.props.hasUserChangedInput) {
      return this.average_category_footprint;
    }

    const { user_footprint, average_footprint } = this.props;
    if (this.props.currentRoute === routes.TAKE_ACTION) {
      return userApiValue('result_takeaction_grand_total', user_footprint, average_footprint);
    } else if (this.props.currentRoute === routes.GET_STARTED) {
      return userApiValue('result_grand_total', user_footprint, average_footprint);
    }
    return graphs.category_keys.reduce((sum, category_key) =>
      sum + parseFloat(userApiValue(category_key, user_footprint, average_footprint)), 0);
  }

  get comparison_byline() {
    if (this.is_worse_than_average) {
      return this.props.t('Worse than Average');
    }
    return this.props.t('Better than Average');
  }

  get category_user_byline() {
    switch (this.props.currentRoute) {
      case routes.GET_STARTED:
        return this.props.t('Your Total Footprint');
      case routes.TRAVEL:
        return this.props.t('Your Travel Footprint');
      case routes.HOME:
        return this.props.t('Your Home Footprint');
      case routes.FOOD:
        return this.props.t('Your Food Footprint');
      case routes.SHOPPING:
        return this.props.t('Your Shopping Footprint');
      default:
        return this.props.t('Total Footprint');
    }
  }

  get category_average_byline() {
    const { averageComparisonLocation, t } = this.props;
    let byline = '';
    switch (this.props.currentRoute) {
      case routes.TRAVEL:
        byline += this.props.t('Average Travel Footprint');
        break;
      case routes.HOME:
        byline += this.props.t('Average Home Footprint');
        break;
      case routes.FOOD:
        byline += this.props.t('Average Food Footprint');
        break;
      case routes.SHOPPING:
        byline += this.props.t('Average Shopping Footprint');
        break;
      default:
        byline += this.props.t('Similar Households');
        break;
    }
    if (averageComparisonLocation) {
      byline += ` ${t('in')} ${averageComparisonLocation}`;
    }
    return byline;
  }

  toggleLock(ev) {
    ev.stopPropagation();
    ev.preventDefault();
    this.setState({
      lockedY: !this.state.lockedY,
    })
  }

  render() {
    const {
      user_footprint, average_footprint, t, hasUserChangedInput,
      averageComparisonLocation,
    } = this.props;

    const userGrandTotal = userApiValue('result_grand_total', user_footprint, average_footprint);
    const averageTotalFootprint = defaultApiValue('result_grand_total', average_footprint);
    const totalPercent = hasUserChangedInput ?
      Math.round(Math.abs(
        ((100 * userGrandTotal) / averageTotalFootprint) - 100,
      )) : 0;
    this.userGrandTotal = userGrandTotal;
    const isWorseThanAverage = Math.round(userGrandTotal) > Math.round(averageTotalFootprint);
    let textAverage = t('Better than Average');
    if (Math.round(totalPercent) === 0) {
      textAverage = t('Same as Average');
    } else if (isWorseThanAverage) {
      textAverage = t('Worse than Average');
    }
    const showAcres = get(this.props.ui, 'widget.options.cta.show_acres', false);
    const totalAcres = userGrandTotal / get(this.props.ui, 'widget.options.cta.acres_per_ton', 1);
    const acresText = get(this.props.ui, 'widget.options.cta.acres_text', 'Acres');

    return (
      <div className="cc-component" id="graphs">
        <div id="graphs_stats">
          <div
            id="graphs__total_footprint"
            className="graphs_stats__block"
          >
            <span className="graphs_stats__block__amount">
              {Math.round(userGrandTotal)}&nbsp;
            </span>
            <span
              className="graphs__units"
              dangerouslySetInnerHTML={{
                __html: t('tons CO<sub>2</sub>eq/year', { ns: 'units' }),
              }}
            />
          </div>
          {showAcres ?
            <div
              id="graphs__total_acres"
              className="graphs_stats__block"
            >
              <span className="graphs_stats__block__amount">{Math.round(totalAcres)}</span>
              <span>{acresText}</span>
            </div>
          :null}
          <div
            id="graphs__total_percent"
            className="graphs_stats__block"
          >
            <span className="graphs_stats__block__amount">{Math.round(totalPercent)} %</span>
            <span>{textAverage}</span>
          </div>
        </div>

        <div>
          <h5
            style={{ marginTop: '15px', marginBottom: '15px' }}
            dangerouslySetInnerHTML={{
              __html: t('Household tons CO<sub>2</sub>eq/year'),
            }}
          />
          <div id="overview_bar_chart" />
          <a href="#" onClick={(ev) => this.toggleLock(ev)} className="graphs__lock_y_axis_btn" title="Locks the y-axis">
            <object type="image/svg+xml" data={this.state.lockedY ? lock : lockOpen} />
          </a>
          <div
            id="graphs__category_comparisons"
            style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginLeft: graphMarginLeft }}
          >
            {[
              'result_transport_total', 'result_housing_total',
              'result_food_total', 'result_goods_total',
              'result_services_total',
            ].map(apiKey => (
              <Label
                id={`graphs__comparison_label__${apiKey}`}
                key={apiKey}
                style={{ width: '50px', textAlign: 'center', fontSize: '90%' }}
                className={isBetterThanAverage(apiKey, user_footprint, average_footprint) ? 'label-success' : 'label-danger'}
              >
                {Math.round(calculateCategoryPercent(apiKey, user_footprint, average_footprint))}%
                <br />
                {isBetterThanAverageText(apiKey, user_footprint, average_footprint, t)}
              </Label>
            ))}
          </div>
          <div style={{ fontStyle: 'italic', margin: '15px' }}>
            <i className="fa fa-map-marker" />
            &nbsp;
            {formatString(t('Compared to households with same size and income in {}.'), `${averageComparisonLocation}`)}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  stackedBarChartData: s.chart.stackedBar(state, ownProps),
  hasUserChangedInput: s.hasUserChangedInput(state),
  averageComparisonLocation: s.averageComparisonLocation(state, ownProps),
  average_footprint: s.average_footprint(state),
  user_footprint: s.user_footprint(state),
  currentRoute: PropTypes.string.isRequired,
  ui: state.ui,
});

export default wrap(
  withRouterAndHelpers,
  withT('graphs'),
  connect(mapStateToProps),
  GraphsComponent,
);
